<template>
  <div class="pts">
    <!-- Full-screen error only before we ever received account/product data -->
    <div v-if="fatalLoadError" class="pts-loading">
      <div class="pts-error">
        <p class="pts-error-title">Could not load product tracking settings</p>
        <p class="pts-error-msg">{{ loadError }}</p>
        <button class="pts-btn pts-btn-outline" @click="fetchSettings">Retry</button>
      </div>
    </div>

    <!-- Shell: filters/table stay mounted (overlay spinner) — avoids search/checkbox remount loops -->
    <div v-else class="pts-shell">
      <div v-show="showLoadingOverlay" class="pts-loading pts-loading--overlay" aria-busy="true">
        <div class="pts-spinner"></div>
      </div>
      <div class="pts-shell-body" :class="{ 'pts-shell-body--inactive': showLoadingOverlay }">
        <div v-if="loadError && !fatalLoadError" class="pts-inline-error">
          <p class="pts-inline-error-msg">{{ loadError }}</p>
          <button type="button" class="pts-btn pts-btn-outline pts-btn-sm" @click="fetchSettings">Retry</button>
        </div>
        <div class="pts-header">
          <h2 class="pts-title">Product Tracking Settings</h2>
          <p class="pts-subtitle">Manage which products SentryKit monitors. Ignored products won't generate alerts but remain in your catalog.</p>
        </div>

      <!-- Filter card: storefront + marketplace + search (search sits to the right of marketplace, mock #1) -->
      <div class="pts-filter-card">
        <p class="pts-filter-hint">Filter by account &amp; marketplace — select one or both to narrow results</p>
        <div class="pts-filter-main">
          <div class="pts-filter-group">
            <label class="pts-filter-label">Storefront</label>
            <div class="pts-pills pts-pills--cols-2">
              <button
                :class="['pts-pill', { active: selectedStorefront === 'all' }]"
                @click="setStorefront('all')"
              >All accounts</button>
              <button
                v-for="acc in storefronts"
                :key="acc.sellerId"
                :class="['pts-pill', { active: String(selectedStorefront) === String(acc.sellerId) }]"
                @click="setStorefront(acc.sellerId)"
              >{{ acc.name }}</button>
            </div>
          </div>
          <div class="pts-filter-divider" aria-hidden="true"></div>
          <div class="pts-filter-group pts-filter-group-mp">
            <label class="pts-filter-label">Marketplace</label>
            <div class="pts-pills pts-pills--cols-2 pts-pills--mp">
              <button
                :class="['pts-pill', { active: selectedMarketplace === 'all' }]"
                @click="setMarketplace('all')"
              >All</button>
              <button
                v-for="mp in availableMarketplaces"
                :key="mp.code"
                :class="['pts-pill', { active: selectedMarketplace === mp.code }]"
                @click="setMarketplace(mp.code)"
              >
                <span v-if="mpEmoji(mp.code)" class="pts-mp-emoji" aria-hidden="true">{{ mpEmoji(mp.code) }}</span>
                <span>{{ mp.label }}</span>
              </button>
            </div>
          </div>
          <div class="pts-filter-search-col">
            <div class="pts-search-wrap">
              <svg class="pts-search-icon" width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"/></svg>
              <input
                id="pts-search-input"
                type="search"
                v-model="searchText"
                v-prevent-autofill="searchText"
                class="pts-search-input"
                placeholder="Search ASIN or name..."
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Status strip below filters: counts left · ignored filter right (matches mock layout) -->
      <div class="pts-summary-bar">
        <span class="pts-summary">
          Showing <strong>{{ summaryFilteredCount }}</strong> products
          &middot; <strong>{{ ignoredCount }}</strong> ignored
          <template v-if="ignoredCount > 0"> &middot; alerts suppressed</template>
        </span>
        <label class="pts-show-ignored">
          <input type="checkbox" v-model="showIgnoredOnly" />
          <span class="pts-show-ignored-box"></span>
          <span>Show ignored only</span>
        </label>
      </div>

      <!-- Bulk ignore over the FULL filtered set (server-side, every page), not just the loaded rows -->
      <div v-if="bulkIgnoreAvailable" class="pts-bulk-ignore">
        <span class="pts-bulk-ignore-scope">All products in <strong>{{ bulkIgnoreScopeLabel }}</strong><template v-if="searchTextTrimmed"> matching “{{ searchTextTrimmed }}”</template>:</span>
        <div class="pts-bulk-ignore-actions">
          <button
            v-if="bulkIgnoreEstimate(true) !== 0"
            type="button"
            class="pts-btn pts-btn-outline pts-btn-sm"
            :disabled="bulkIgnoreBusy || hasChanges"
            :title="hasChanges ? 'Save or discard your changes first' : ''"
            @click="startBulkIgnore(true)"
          >{{ bulkIgnoreButtonLabel(true) }}</button>
          <button
            v-if="bulkIgnoreEstimate(false) !== 0"
            type="button"
            class="pts-btn pts-btn-outline pts-btn-sm"
            :disabled="bulkIgnoreBusy || hasChanges"
            :title="hasChanges ? 'Save or discard your changes first' : ''"
            @click="startBulkIgnore(false)"
          >{{ bulkIgnoreButtonLabel(false) }}</button>
        </div>
        <p v-if="bulkIgnoreMessage" class="pts-bulk-result" :class="'pts-bulk-result--' + bulkIgnoreLevel">{{ bulkIgnoreMessage }}</p>
      </div>

      <!-- Bulk CSV: asin, marketplace, ignore -->
      <div class="pts-bulk-card">
        <h3 class="pts-bulk-title">Bulk update (CSV)</h3>
        <p class="pts-bulk-desc">
          Upload a CSV with headers <strong>asin</strong>, <strong>marketplace</strong>, and <strong>ignore</strong>.
          Marketplace codes match the filters (e.g. <code>us</code>, <code>uk</code>, <code>de</code>).
          <strong>ignore</strong>: <code>true</code> / <code>false</code>, <code>yes</code> / <code>no</code>, or <code>1</code> / <code>0</code>.
        </p>
        <div class="pts-bulk-actions">
          <button type="button" class="pts-btn pts-btn-outline pts-btn-sm" :disabled="bulkCsvDownloading" @click="downloadBulkCsvExport">{{ bulkCsvDownloading ? 'Downloading…' : 'Download product CSV' }}</button>
          <label class="pts-btn pts-btn-outline pts-btn-sm pts-bulk-file-label" :class="{ 'pts-bulk-file-label--busy': bulkCsvUploading }">
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              class="pts-bulk-file-input"
              :disabled="bulkCsvUploading"
              @change="onBulkCsvFile"
            />
            <span class="pts-bulk-file-text">{{ bulkCsvUploading ? 'Uploading…' : 'Upload CSV' }}</span>
          </label>
        </div>
        <p v-if="bulkCsvResult" class="pts-bulk-result" :class="bulkCsvResultLevel">{{ bulkCsvResult }}</p>
        <ul v-if="bulkCsvErrors.length" class="pts-bulk-errors">
          <li v-for="(errLine, idx) in bulkCsvErrors" :key="'bulk-csv-err-' + idx">{{ errLine }}</li>
        </ul>
      </div>

      <!-- Table -->
      <div class="pts-table-wrap">
        <table class="pts-table">
          <thead>
            <tr>
              <th class="pts-th-product">Product</th>
              <th class="pts-th-storefront">Storefront</th>
              <th class="pts-th-mp">Marketplace</th>
              <th class="pts-th-shortname">Short-hand name</th>
              <th class="pts-th-ignore">Ignore</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="tableRows.length === 0">
              <td colspan="5" class="pts-empty">No products match the current filters.</td>
            </tr>
            <tr v-for="(product, rowIdx) in tableRows" :key="product.productId != null ? product.productId : ('pts-row-' + rowIdx)" class="pts-row">
              <td class="pts-cell-product">
                <div class="pts-product-wrap">
                  <a :href="product.productPage" target="_blank" rel="noopener noreferrer" class="pts-product-img-link">
                    <img
                      v-if="product.imageUrl"
                      :src="product.imageUrl"
                      :alt="product.productName || ''"
                      class="pts-product-img"
                    />
                    <div v-else class="pts-product-img-placeholder">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/></svg>
                    </div>
                  </a>
                  <div class="pts-product-info">
                    <span class="pts-product-name">{{ product.productName || product.asin1 }}</span>
                    <span class="pts-product-asin">{{ product.asin1 }}</span>
                  </div>
                </div>
              </td>
              <td class="pts-cell-storefront">{{ storefrontNameFor(product) }}</td>
              <td class="pts-cell-mp">
                <span class="pts-mp-badge">
                  <span v-if="mpEmoji(product.marketplaceIso31662Code)" class="pts-mp-emoji" aria-hidden="true">{{ mpEmoji(product.marketplaceIso31662Code) }}</span>
                  {{ mpLabel(product.marketplaceIso31662Code) }}
                </span>
              </td>
              <td class="pts-cell-shortname">
                <input
                  type="text"
                  :value="product.shortName"
                  @input="onShortNameInput(product, $event.target.value)"
                  class="pts-shortname-input"
                  autocomplete="off"
                  data-lpignore="true"
                />
              </td>
              <td class="pts-cell-ignore">
                <label class="pts-toggle">
                  <input type="checkbox" :checked="!product.show" @change="onToggleIgnore(product)" />
                  <span class="pts-toggle-slider"></span>
                </label>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pts-pagination">
        <span class="pts-page-label">Page {{ currentPage + 1 }} of {{ totalPages }}</span>
        <div class="pts-page-controls">
          <button class="pts-page-btn" :disabled="currentPage === 0" @click="goToPage(0)">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/></svg>
          </button>
          <button class="pts-page-btn" :disabled="currentPage === 0" @click="goToPage(currentPage - 1)">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
          </button>
          <span class="pts-page-num">{{ currentPage + 1 }} / {{ totalPages }}</span>
          <button class="pts-page-btn" :disabled="currentPage >= totalPages - 1" @click="goToPage(currentPage + 1)">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/></svg>
          </button>
          <button class="pts-page-btn" :disabled="currentPage >= totalPages - 1" @click="goToPage(totalPages - 1)">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0zm-4 0a1 1 0 010-1.414L10.586 10 6.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clip-rule="evenodd"/></svg>
          </button>
        </div>
      </div>

      <!-- Unsaved changes bar -->
      <transition name="pts-slide-up">
        <div v-if="hasChanges" class="pts-changes-bar">
          <span class="pts-changes-text">
            <span class="pts-changes-dot"></span>
            You have unsaved changes
          </span>
          <div class="pts-changes-actions">
            <button class="pts-btn pts-btn-outline" @click="discard">Discard</button>
            <button class="pts-btn pts-btn-primary" :class="{ 'pts-btn-loading': saving }" @click="save">Save changes</button>
          </div>
        </div>
      </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { extractMarketplaceCodesFromV1SellersBody } from '../../utils/sellerAccountsFromV1Api.js'

var MARKETPLACE_LABELS = {
  us: 'US', uk: 'UK', gb: 'UK', ca: 'CA', de: 'DE', fr: 'FR',
  it: 'IT', es: 'ES', jp: 'JP', au: 'AU', 'in': 'IN', mx: 'MX',
  br: 'BR', nl: 'NL', sa: 'SA', ae: 'AE', sg: 'SG', se: 'SE',
  pl: 'PL', be: 'BE', at: 'AT', za: 'ZA', tr: 'TR', eg: 'EG'
}

/** Region emoji for filter + table (mirrors client static HTML e.g. 🇺🇸 US) */
var MP_EMOJI = {
  us: '🇺🇸', uk: '🇬🇧', gb: '🇬🇧', ca: '🇨🇦', de: '🇩🇪', fr: '🇫🇷',
  it: '🇮🇹', es: '🇪🇸', jp: '🇯🇵', au: '🇦🇺', 'in': '🇮🇳', mx: '🇲🇽',
  br: '🇧🇷', nl: '🇳🇱', sa: '🇸🇦', ae: '🇦🇪', sg: '🇸🇬', se: '🇸🇪',
  pl: '🇵🇱', be: '🇧🇪', at: '🇦🇹', za: '🇿🇦', tr: '🇹🇷', eg: '🇪🇬'
}

/** Compare against API/market selects — always lowercase */
function normalizeMarketplaceCode (code) {
  return code != null ? String(code).trim().toLowerCase() : ''
}

export default {
  name: 'ProductTrackingSettings',
  data: function () {
    return {
      sellerAccounts: [],
      allProducts: [],
      productMap: {},
      accountsSetupPending: [],

      selectedStorefront: 'all',
      selectedMarketplace: 'all',
      searchText: '',
      showIgnoredOnly: false,

      currentPage: 0,
      /** Initial page size for GET; server-driven list overwrites from each response (API may cap, e.g. 30). */
      pageSize: 30,

      automaticallyTrackNewProducts: false,
      originalAutomaticallyTrackNewProducts: false,

      loading: false,
      saving: false,
      loadError: null,

      bulkCsvResult: '',
      bulkCsvResultLevel: '',
      bulkCsvErrors: [],
      bulkCsvUploading: false,
      bulkCsvDownloading: false,

      /** When true, GET /api/product/settings applies filters/pagination on the server (see buildProductSettingsQueryParams). */
      useServerList: false,
      serverTotalFiltered: 0,
      serverTotalPages: 1,
      serverIgnoredCount: null,
      /** Lowercase marketplace codes from API for filter pills when useServerList (avoids scanning full catalog client-side). */
      serverMarketplaceCodes: null,
      /** Lowercase codes from GET /api/v1/sellers (connected seller marketplaces); merged with serverMarketplaceCodes for pills. */
      sellerApiMarketplaceCodes: null,
      searchDebounceTimer: null,
      hasLoadedOnce: false,
      /** Count of in-flight list GETs; each completion decrements so loading never sticks. */
      _listInflight: 0,
      _listRequestId: 0,
      _listHttpPromise: null,
      _listLoadTimer: null,
      /** Suppress filter watchers while applying API response (avoids overlapping fetches). */
      _ingestingListResponse: false,
      bulkIgnoreBusy: false,
      bulkIgnoreMessage: '',
      bulkIgnoreLevel: 'ok'
    }
  },
  computed: {
    /** No accounts/products yet → full-screen error; otherwise show inline retry. */
    showLoadingOverlay: function () {
      return this.loading && !this.hasLoadedOnce
    },
    fatalLoadError: function () {
      if (!this.loadError || this.loading) return false
      var acc = this.sellerAccounts || []
      var rows = this.allProducts || []
      return acc.length === 0 && rows.length === 0
    },
    storefronts: function () {
      return this.sellerAccounts.map(function (acc) {
        return { sellerId: acc.sellerId, name: acc.name }
      })
    },
    /**
     * Marketplace pills: union of product-settings `availableMarketplaces` (server list), GET /api/v1/sellers,
     * and (last) marketplaces seen on loaded product rows for the selected storefront.
     */
    availableMarketplaces: function () {
      var self = this
      var seen = {}
      var ordered = []
      function addCodes (arr) {
        if (!Array.isArray(arr)) return
        for (var i = 0; i < arr.length; i++) {
          var c = normalizeMarketplaceCode(arr[i])
          if (!c || seen[c]) continue
          seen[c] = true
          ordered.push(c)
        }
      }
      if (this.useServerList) addCodes(this.serverMarketplaceCodes)
      addCodes(this.sellerApiMarketplaceCodes)
      var base = this.selectedStorefront === 'all'
        ? this.allProducts
        : this.allProducts.filter(function (p) { return String(p._sellerId) === String(self.selectedStorefront) })
      for (var j = 0; j < base.length; j++) {
        var c = normalizeMarketplaceCode(base[j].marketplaceIso31662Code)
        if (!c || seen[c]) continue
        seen[c] = true
        ordered.push(c)
      }
      var out = []
      for (var k = 0; k < ordered.length; k++) {
        var code = ordered[k]
        out.push({ code: code, label: MARKETPLACE_LABELS[code] || code.toUpperCase() })
      }
      return out
    },
    /** Client-side filter/sort only when legacy full payload (useServerList false). */
    filteredProducts: function () {
      if (this.useServerList) return this.allProducts
      var self = this
      var products = this.allProducts

      if (this.selectedStorefront !== 'all') {
        var sf = String(this.selectedStorefront)
        products = products.filter(function (p) { return String(p._sellerId) === sf })
      }
      if (this.selectedMarketplace !== 'all') {
        products = products.filter(function (p) {
          return normalizeMarketplaceCode(p.marketplaceIso31662Code) === self.selectedMarketplace
        })
      }
      if (this.showIgnoredOnly) {
        products = products.filter(function (p) { return !p.show })
      }
      if (this.searchText) {
        var terms = this.searchText.toLowerCase().split(/\s+/).filter(function (t) { return t.length > 0 })
        var pattern = new RegExp('^.*' + terms.map(function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') }).join('.*') + '.*$')
        products = products.filter(function (p) {
          return (p.asin1 && pattern.test(p.asin1.toLowerCase())) ||
            (p.shortName && pattern.test(p.shortName.toLowerCase())) ||
            (p.productName && pattern.test(p.productName.toLowerCase()))
        })
      }
      return products
    },
    totalPages: function () {
      if (this.useServerList) {
        var n = this.serverTotalFiltered > 0 ? this.serverTotalFiltered : this.allProducts.length
        return Math.max(1, Math.ceil(n / this.pageSize))
      }
      return Math.max(1, Math.ceil(this.filteredProducts.length / this.pageSize))
    },
    tableRows: function () {
      if (this.useServerList) return this.allProducts
      var start = this.currentPage * this.pageSize
      return this.filteredProducts.slice(start, start + this.pageSize)
    },
    summaryFilteredCount: function () {
      if (this.useServerList) return this.serverTotalFiltered
      return this.filteredProducts.length
    },
    ignoredCount: function () {
      if (this.useServerList && this.serverIgnoredCount != null) return this.serverIgnoredCount
      return this.allProducts.filter(function (p) { return !p.show }).length
    },
    searchTextTrimmed: function () {
      return (this.searchText || '').trim()
    },
    /** Bulk ignore needs a storefront and/or marketplace filter, so one click can't hit the whole catalog. */
    bulkIgnoreAvailable: function () {
      return this.useServerList && (this.selectedStorefront !== 'all' || this.selectedMarketplace !== 'all')
    },
    bulkIgnoreScopeLabel: function () {
      var parts = []
      if (this.selectedStorefront !== 'all') {
        var sf = String(this.selectedStorefront)
        var acc = this.storefronts.filter(function (a) { return String(a.sellerId) === sf })[0]
        parts.push(acc ? acc.name : 'this storefront')
      } else {
        parts.push('all storefronts')
      }
      parts.push(this.selectedMarketplace !== 'all' ? this.mpLabel(this.selectedMarketplace) : 'all marketplaces')
      return parts.join(' · ')
    },
    hasChanges: function () {
      if (this.automaticallyTrackNewProducts !== this.originalAutomaticallyTrackNewProducts) return true
      var ids = Object.keys(this.productMap)
      for (var i = 0; i < ids.length; i++) {
        var p = this.productMap[ids[i]]
        if (p.shortName !== p._originalShortName) return true
        if (p.show !== p._originalShow) return true
        if (p.active !== p._originalActive) return true
      }
      return false
    }
  },
  watch: {
    searchText: function (val, prev) {
      if (val === prev) return
      if (this._ingestingListResponse) return
      this.currentPage = 0
      this.scheduleReloadFromFilters()
    },
    selectedStorefront: function () {
      if (this._ingestingListResponse) return
      this.currentPage = 0
      this.loadProductTrackingPage({ resetPage: true })
    },
    selectedMarketplace: function () {
      if (this._ingestingListResponse) return
      this.currentPage = 0
      this.loadProductTrackingPage({ resetPage: true })
    },
    showIgnoredOnly: function (val, prev) {
      if (val === prev) return
      if (this._ingestingListResponse) return
      this.currentPage = 0
      this.loadProductTrackingPage({ resetPage: true })
    },
    hasChanges: function (val) {
      window.dirty = val
    }
  },
  created: function () {
    window.dirty = false
  },
  mounted: function () {
    this.loadSellerMarketplaces()
    this.loadProductTrackingPage({ resetPage: true })
  },
  beforeDestroy: function () {
    if (this._bulkIgnoreMsgTimer) clearTimeout(this._bulkIgnoreMsgTimer)
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = null
    }
    if (this._listLoadTimer) {
      clearTimeout(this._listLoadTimer)
      this._listLoadTimer = null
    }
    this.abortListRequest()
  },
  methods: {
    storefrontNameFor: function (product) {
      if (product._sellerName) return product._sellerName
      var sid = String(product._sellerId)
      var acc = this.storefronts.filter(function (a) { return String(a.sellerId) === sid })[0]
      return acc ? acc.name : '—'
    },
    /**
     * Products a bulk action would change, from the counts already on screen. Exact only without a search or
     * "ignored only" (the summary's ignored count ignores both); otherwise null and the confirm step asks the server.
     */
    bulkIgnoreEstimate: function (ignore) {
      if (this.searchTextTrimmed || this.showIgnoredOnly || this.serverIgnoredCount == null) return null
      var ignored = Number(this.serverIgnoredCount) || 0
      var total = Number(this.serverTotalFiltered) || 0
      return ignore ? Math.max(0, total - ignored) : ignored
    },
    bulkIgnoreButtonLabel: function (ignore) {
      var n = this.bulkIgnoreEstimate(ignore)
      var verb = ignore ? 'Ignore' : 'Un-ignore'
      if (n == null) return verb + ' all'
      return verb + ' all ' + n.toLocaleString() + ' product' + (n === 1 ? '' : 's')
    },
    bulkIgnoreBody: function (ignore, dryRun) {
      var body = { ignore: !!ignore }
      if (this.selectedStorefront !== 'all') body.sellerAccount = String(this.selectedStorefront)
      if (this.selectedMarketplace !== 'all') body.marketplace = this.selectedMarketplace
      if (this.searchTextTrimmed) body.q = this.searchTextTrimmed
      if (dryRun) body.dryRun = true
      return body
    },
    /** Ask the server for the exact count, confirm, then apply to every matching product. */
    startBulkIgnore: function (ignore) {
      var self = this
      if (this.bulkIgnoreBusy || this.hasChanges) return
      this.bulkIgnoreBusy = true
      this.bulkIgnoreMessage = ''
      this.$http.post('/api/product/settings/bulk-ignore', this.bulkIgnoreBody(ignore, true)).then(function (res) {
        var n = Number((res.body || {}).matched) || 0
        if (!n) {
          self.bulkIgnoreBusy = false
          self.setBulkIgnoreMessage('Nothing to change — every matching product is already ' + (ignore ? 'ignored' : 'tracked') + '.', 'ok')
          return
        }
        var scope = self.bulkIgnoreScopeLabel + (self.searchTextTrimmed ? ' matching “' + self.searchTextTrimmed + '”' : '')
        var noun = n.toLocaleString() + ' product' + (n === 1 ? '' : 's')
        var question = ignore
          ? 'This will stop alerts for ' + noun + ' in ' + scope + '. Continue?'
          : 'This will start tracking and alerting on ' + noun + ' in ' + scope + ' again. Continue?'
        if (!window.confirm(question)) {
          self.bulkIgnoreBusy = false
          return
        }
        return self.$http.post('/api/product/settings/bulk-ignore', self.bulkIgnoreBody(ignore, false)).then(function (applied) {
          var body = applied.body || {}
          self.bulkIgnoreBusy = false
          if (body.queued) {
            self.setBulkIgnoreMessage(
              (ignore ? 'Ignoring ' : 'Un-ignoring ') + noun + ' in the background. Counts update when it finishes.',
              'ok'
            )
            setTimeout(function () { self.loadProductTrackingPage({ resetPage: false }) }, 5000)
          } else {
            var done = Number(body.updated) || 0
            self.setBulkIgnoreMessage(
              (ignore ? 'Ignored ' : 'Un-ignored ') + done.toLocaleString() + ' product' + (done === 1 ? '' : 's') + '.',
              'ok'
            )
          }
          self.loadProductTrackingPage({ resetPage: false })
        })
      }).catch(function (err) {
        self.bulkIgnoreBusy = false
        var msg = (err && err.body && (err.body.error || err.body.message)) || 'Could not update products.'
        self.setBulkIgnoreMessage(msg, 'err')
      })
    },
    setBulkIgnoreMessage: function (msg, level) {
      var self = this
      this.bulkIgnoreMessage = msg
      this.bulkIgnoreLevel = level || 'ok'
      if (this._bulkIgnoreMsgTimer) clearTimeout(this._bulkIgnoreMsgTimer)
      this._bulkIgnoreMsgTimer = setTimeout(function () { self.bulkIgnoreMessage = '' }, 8000)
    },
    mpLabel: function (code) {
      var c = normalizeMarketplaceCode(code)
      return MARKETPLACE_LABELS[c] || (c ? c.toUpperCase() : '')
    },
    mpEmoji: function (code) {
      var c = normalizeMarketplaceCode(code)
      return MP_EMOJI[c] || ''
    },
    setStorefront: function (val) {
      this.selectedStorefront = val === 'all' ? 'all' : String(val)
    },
    setMarketplace: function (val) {
      this.selectedMarketplace = val
    },
    abortListRequest: function () {
      var p = this._listHttpPromise
      if (p && typeof p.abort === 'function') {
        try { p.abort() } catch (e) { /* noop */ }
      }
      this._listHttpPromise = null
    },
    /** Query string for GET /api/product/settings — product-tracking page uses paginated `for` mode. */
    buildProductSettingsQueryParams: function () {
      var params = {
        for: 'product-tracking-settings',
        page: String(this.currentPage),
        pageSize: String(this.pageSize)
      }
      if (this.selectedStorefront !== 'all') params.sellerAccount = String(this.selectedStorefront)
      if (this.selectedMarketplace !== 'all') params.marketplace = this.selectedMarketplace
      var q = (this.searchText || '').trim()
      if (q) params.q = q
      if (this.showIgnoredOnly) params.ignoredOnly = '1'
      return params
    },
    normalizeProductRow: function (p, fallbackSellerId, fallbackSellerName) {
      if (p.productId == null && p.product_id != null) p.productId = p.product_id
      if (p.productId == null && p.id != null) p.productId = p.id
      if (p.marketplaceIso31662Code == null && p.marketplace_iso31662_code != null) {
        p.marketplaceIso31662Code = p.marketplace_iso31662_code
      }
      if (p._sellerId == null && p.sellerId != null) p._sellerId = p.sellerId
      if (p._sellerId == null && p.seller_id != null) p._sellerId = p.seller_id
      if (p._sellerId == null && fallbackSellerId != null) p._sellerId = fallbackSellerId
      if (p._sellerId != null) p._sellerId = String(p._sellerId)
      if (p._sellerName == null && p.sellerName != null) p._sellerName = p.sellerName
      if (p._sellerName == null && fallbackSellerName != null) p._sellerName = fallbackSellerName
      if (p._originalShortName === undefined) p._originalShortName = p.shortName
      if (p._originalShow === undefined) p._originalShow = p.show
      if (p._originalActive === undefined) p._originalActive = p.active
    },
    scheduleReloadFromFilters: function () {
      var self = this
      this.currentPage = 0
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(function () {
        self.searchDebounceTimer = null
        self.loadProductTrackingPage({ resetPage: true })
      }, 400)
    },
    goToPage: function (page) {
      var max = Math.max(0, this.totalPages - 1)
      var p = Math.max(0, Math.min(max, page))
      if (p === this.currentPage) return
      this.currentPage = p
      this.loadProductTrackingPage({ resetPage: false })
    },
    /** GET /api/v1/sellers — marketplace codes connected to seller accounts (merged with product-settings availableMarketplaces). */
    loadSellerMarketplaces: function () {
      var self = this
      this.$http.get('/api/v1/sellers').then(
        function (res) {
          var body = (res && (res.body != null ? res.body : res.data)) || {}
          var raw = extractMarketplaceCodesFromV1SellersBody(body)
          var seen = {}
          var out = []
          for (var i = 0; i < raw.length; i++) {
            var c = normalizeMarketplaceCode(raw[i])
            if (!c || seen[c]) continue
            seen[c] = true
            out.push(c)
          }
          self.sellerApiMarketplaceCodes = out
        },
        function () {
          if (self.sellerApiMarketplaceCodes == null) self.sellerApiMarketplaceCodes = []
        }
      )
    },
    /**
     * When the API wraps accounts in `data` / `payload` / `result` but leaves paginated `products`
     * and metadata on the outer object, unwrapping would drop them and the UI shows an empty table.
     */
    mergeUnwrappedProductSettingsBody: function (top, inner) {
      if (!top || !inner || top === inner) return inner
      var merged = inner
      function copyIfNeeded () {
        if (merged === inner) merged = Object.assign({}, inner)
      }
      var keys = [
        'products', 'productList',
        'totalFiltered', 'total_filtered',
        'totalPages', 'total_pages',
        'currentPage', 'current_page',
        'pageSize', 'page_size',
        'ignoredCount', 'ignored_count',
        'totalIgnored', 'total_ignored',
        'availableMarketplaces', 'available_marketplaces',
        'listMode', 'list_mode',
        'pagination',
        'automaticallyTrackNewProducts', 'automatically_track_new_products'
      ]
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i]
        var innerVal = merged[k]
        var topVal = top[k]
        if (topVal == null) continue
        var innerMissing = innerVal == null
        var innerProductsEmpty = k === 'products' && Array.isArray(innerVal) && innerVal.length === 0
        var innerListEmpty = k === 'productList' && Array.isArray(innerVal) && innerVal.length === 0
        if (innerMissing || innerProductsEmpty || innerListEmpty) {
          copyIfNeeded()
          merged[k] = topVal
        }
      }
      return merged
    },
    /** vue-resource uses `body`; axios-style clients use `data`; body may be a JSON string. */
    parseSettingsResponseFromHttp: function (response) {
      if (!response) return {}
      var raw = response.body != null ? response.body : response.data
      if (raw == null && typeof response.bodyText === 'string' && response.bodyText.length) {
        try {
          raw = JSON.parse(response.bodyText)
        } catch (e) {
          raw = null
        }
      }
      if (raw == null) return {}
      if (typeof raw === 'string') {
        try {
          raw = JSON.parse(raw)
        } catch (e) {
          return {}
        }
      }
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {}
      var top = raw
      /* Some gateways wrap the JSON in `{ data: { ... } }`. */
      var inner = raw.data
      if (inner != null && typeof inner === 'object' && !Array.isArray(inner) &&
          (inner.sellerAccounts != null || inner.products != null || inner.listMode != null || inner.list_mode != null)) {
        raw = this.mergeUnwrappedProductSettingsBody(top, inner)
      } else {
        inner = raw.payload
        if (inner != null && typeof inner === 'object' && !Array.isArray(inner) &&
            (inner.sellerAccounts != null || inner.products != null)) {
          raw = this.mergeUnwrappedProductSettingsBody(top, inner)
        } else {
          inner = raw.result
          if (inner != null && typeof inner === 'object' && !Array.isArray(inner) &&
              (inner.sellerAccounts != null || inner.products != null)) {
            raw = this.mergeUnwrappedProductSettingsBody(top, inner)
          }
        }
      }
      return raw
    },
    loadProductTrackingPage: function (options) {
      var opts = options || {}
      if (opts.resetPage) this.currentPage = 0
      var reqId = ++this._listRequestId
      this.abortListRequest()
      if (this._listLoadTimer) {
        clearTimeout(this._listLoadTimer)
        this._listLoadTimer = null
      }
      this._listInflight = (this._listInflight || 0) + 1
      this.loading = true
      this.loadError = null
      this.bulkCsvResult = ''
      this.bulkCsvResultLevel = ''
      this.bulkCsvErrors = []
      var self = this
      var finishedThisRequest = false
      var finishListRequest = function () {
        if (finishedThisRequest) return
        finishedThisRequest = true
        if (self._listLoadTimer) {
          clearTimeout(self._listLoadTimer)
          self._listLoadTimer = null
        }
        if (reqId === self._listRequestId) self._listHttpPromise = null
        self._listInflight = Math.max(0, (self._listInflight || 1) - 1)
        if (self._listInflight === 0) self.loading = false
      }
      this.$http.get('/api/product/settings', { params: this.buildProductSettingsQueryParams() }).then(
        function (response) {
          self._ingestingListResponse = true
          try {
            self.ingestSettingsResponse(self.parseSettingsResponseFromHttp(response))
            self.loadError = null
            self.hasLoadedOnce = true
          } catch (e) {
            console.error('[ProductTrackingSettings] ingest failed', e)
            self.loadError = 'Could not read server response.'
          } finally {
            self._ingestingListResponse = false
          }
          finishListRequest()
        },
        function (err) {
          if (reqId !== self._listRequestId) {
            finishListRequest()
            return
          }
          if (err && err.status === 0) {
            finishListRequest()
            return
          }
          var status = err && err.status
          if (status === 401 || status === 403) {
            self.loadError = (err.body && err.body.message) || err.statusText || 'Request failed'
            finishListRequest()
            return
          }
          self.loadError = (err.body && err.body.message) || err.statusText || 'Request failed'
          finishListRequest()
        }
      )
    },
    /** Server-driven paginated list from GET /api/product/settings?for=product-tracking-settings */
    ingestSettingsResponse: function (body) {
      var accounts = body.sellerAccounts || body.seller_accounts || []
      this.sellerAccounts = accounts
      this.accountsSetupPending = accounts.filter(function (a) { return !a.setupCompleted }).map(function (a) { return a.sellerId })
      this.originalAutomaticallyTrackNewProducts = !!(body.automaticallyTrackNewProducts != null ? body.automaticallyTrackNewProducts : body.automatically_track_new_products)
      this.automaticallyTrackNewProducts = this.originalAutomaticallyTrackNewProducts

      var pag = body.pagination || {}
      var totalFilteredRaw = body.totalFiltered != null ? body.totalFiltered : body.total_filtered
      if (totalFilteredRaw == null) totalFilteredRaw = pag.totalItems != null ? pag.totalItems : pag.total_items
      var totalPagesRaw = body.totalPages != null ? body.totalPages : body.total_pages
      if (totalPagesRaw == null) totalPagesRaw = pag.totalPages != null ? pag.totalPages : pag.total_pages
      var pageSizeRaw = body.pageSize != null ? body.pageSize : body.page_size
      if (pageSizeRaw == null) pageSizeRaw = pag.pageSize != null ? pag.pageSize : pag.page_size
      var curPageRaw = body.currentPage != null ? body.currentPage : body.current_page
      if (curPageRaw == null) curPageRaw = pag.currentPage != null ? pag.currentPage : pag.current_page

      var totalFiltered = totalFilteredRaw != null ? Number(totalFilteredRaw) : null
      var totalPages = totalPagesRaw != null ? Number(totalPagesRaw) : null
      var pageSizeResp = pageSizeRaw != null ? Number(pageSizeRaw) : null
      var curPageResp = curPageRaw != null ? Number(curPageRaw) : null

      var rootProducts = Array.isArray(body.products)
        ? body.products
        : (Array.isArray(body.productList) ? body.productList : [])

      this.useServerList = true
      if (pageSizeResp != null && !isNaN(pageSizeResp) && pageSizeResp > 0) this.pageSize = pageSizeResp
      if (curPageResp != null && !isNaN(curPageResp)) this.currentPage = curPageResp

      if (totalFiltered != null && !isNaN(totalFiltered)) {
        this.serverTotalFiltered = totalFiltered
      } else if (totalPages != null && !isNaN(totalPages) && curPageResp != null && !isNaN(curPageResp) && curPageResp >= totalPages - 1) {
        this.serverTotalFiltered = (totalPages - 1) * this.pageSize + rootProducts.length
      } else {
        this.serverTotalFiltered = rootProducts.length
      }

      if (totalPages != null && !isNaN(totalPages)) this.serverTotalPages = Math.max(1, totalPages)
      else {
        this.serverTotalPages = Math.max(1, Math.ceil(this.serverTotalFiltered / this.pageSize))
      }

      var ignRaw = body.ignoredCount != null ? body.ignoredCount : body.ignored_count
      if (ignRaw == null) ignRaw = body.totalIgnored != null ? body.totalIgnored : body.total_ignored
      var ign = ignRaw != null ? Number(ignRaw) : null
      this.serverIgnoredCount = ign != null && !isNaN(ign) ? ign : null

      var mps = body.availableMarketplaces != null ? body.availableMarketplaces : body.available_marketplaces
      if (Array.isArray(mps) && mps.length) {
        this.serverMarketplaceCodes = mps.map(function (x) {
          return normalizeMarketplaceCode(typeof x === 'string' ? x : (x.code || x.marketplace || ''))
        }).filter(Boolean)
      } else if (!(this.serverMarketplaceCodes && this.serverMarketplaceCodes.length)) {
        this.serverMarketplaceCodes = null
      }

      this.productMap = {}
      for (var i = 0; i < rootProducts.length; i++) {
        var row = rootProducts[i]
        this.normalizeProductRow(row, null, null)
        this.$set(this.productMap, row.productId, row)
      }
      this.allProducts = rootProducts
    },
    fetchSettings: function () {
      this.loadSellerMarketplaces()
      this.loadProductTrackingPage({ resetPage: true })
    },
    onShortNameInput: function (product, value) {
      this.$set(product, 'shortName', value)
    },
    onToggleIgnore: function (product) {
      if (product.show) {
        this.$set(product, 'show', false)
        this.$set(product, 'active', false)
      } else {
        this.$set(product, 'show', true)
        this.$set(product, 'active', true)
      }
    },
    save: function () {
      var result = []
      var productIds = Object.keys(this.productMap)
      for (var i = 0; i < productIds.length; i++) {
        var productId = productIds[i]
        var p = this.productMap[productId]
        var shortNameChanged = p.shortName !== p._originalShortName
        var showChanged = p.show !== p._originalShow || p._originalActive !== p.active
        if (shortNameChanged || showChanged) {
          var r = { productId: productId }
          if (shortNameChanged) r.shortName = p.shortName
          if (showChanged) {
            if (p.active === null) {
              r.ignoredUntilNextOrder = true
              r.show = false
            } else {
              if (p._originalActive === null) r.ignoredUntilNextOrder = false
              r.show = p.show
            }
          }
          result.push(r)
        }
      }
      var data = {
        products: result,
        accountsSetupDone: this.accountsSetupPending,
        automaticallyTrackNewProducts: this.automaticallyTrackNewProducts !== this.originalAutomaticallyTrackNewProducts ? this.automaticallyTrackNewProducts : null
      }
      var hadPending = this.accountsSetupPending.length > 0
      this.saving = true
      this.$http.put('/api/product/settings', data).then(
        function () {
          var pmap = this.productMap
          var ids = Object.keys(pmap)
          for (var i = 0; i < ids.length; i++) {
            var p = pmap[ids[i]]
            p._originalShortName = p.shortName
            p._originalShow = p.show
            p._originalActive = p.active
          }
          this.originalAutomaticallyTrackNewProducts = this.automaticallyTrackNewProducts
          this.accountsSetupPending = []
          if (hadPending) this.$root.anySellerAccountSetupCompleted = true
          this.saving = false
          window.dirty = false
          if (this.hasChanges) {
            this.loadProductTrackingPage({ resetPage: false })
          }
        }.bind(this),
        function () {
          this.saving = false
        }.bind(this)
      )
    },
    discard: function () {
      var pmap = this.productMap
      var ids = Object.keys(pmap)
      for (var i = 0; i < ids.length; i++) {
        var p = pmap[ids[i]]
        this.$set(p, 'shortName', p._originalShortName)
        this.$set(p, 'show', p._originalShow)
        this.$set(p, 'active', p._originalActive)
      }
      this.automaticallyTrackNewProducts = this.originalAutomaticallyTrackNewProducts
      window.dirty = false
      this.bulkCsvResult = ''
      this.bulkCsvResultLevel = ''
      this.bulkCsvErrors = []
    },
    isBulkCsvFile: function (file) {
      if (!file || !file.name) return false
      if (!/\.csv$/i.test(file.name)) return false
      if (!file.type) return true
      return file.type === 'text/csv' ||
        file.type === 'application/csv' ||
        file.type === 'text/plain' ||
        file.type === 'application/octet-stream'
    },
    setBulkCsvResult: function (message, level, errors) {
      this.bulkCsvResult = message || ''
      this.bulkCsvErrors = Array.isArray(errors) ? errors.map(function (e) { return String(e) }) : []
      this.bulkCsvResultLevel = level === 'ok'
        ? 'pts-bulk-result--ok'
        : level === 'warn'
          ? 'pts-bulk-result--warn'
          : level === 'err'
            ? 'pts-bulk-result--err'
            : ''
    },
    bulkCsvErrorsFromBody: function (body) {
      if (!body || typeof body !== 'object') return []
      var list = []
      if (body.results && Array.isArray(body.results.errors)) list = body.results.errors
      else if (Array.isArray(body.errors)) list = body.errors
      return list.map(function (e) { return String(e) })
    },
    bulkCsvMessageFromBody: function (body, fallbackOk) {
      if (!body || typeof body !== 'object') return fallbackOk
      if (body.message != null && String(body.message).trim() !== '') return String(body.message).trim()
      if (body.error != null && String(body.error).trim() !== '') return String(body.error).trim()
      if (body.detail != null && String(body.detail).trim() !== '') return String(body.detail).trim()
      return fallbackOk
    },
    bulkCsvSummaryFromBody: function (body, errors) {
      var results = body && body.results
      if (results && typeof results === 'object') {
        var parts = []
        if (results.total_rows != null) parts.push(results.total_rows + ' row(s)')
        if (results.updated != null) parts.push(results.updated + ' updated')
        if (results.skipped != null) parts.push(results.skipped + ' skipped')
        if (parts.length) {
          var summary = 'Import finished: ' + parts.join(', ') + '.'
          if (errors.length) return summary
          return summary
        }
      }
      if (errors.length) return 'Import completed with errors.'
      return this.bulkCsvMessageFromBody(body, 'CSV imported successfully.')
    },
    normalizeBulkCsvImportBody: function (rawBody) {
      if (rawBody == null) return {}
      if (typeof rawBody === 'string') {
        var trimmed = rawBody.trim()
        if (trimmed.indexOf('<!DOCTYPE') === 0 || trimmed.indexOf('<html') === 0) return {}
        try {
          return JSON.parse(trimmed)
        } catch (e) {
          return {}
        }
      }
      if (typeof rawBody !== 'object' || Array.isArray(rawBody)) return {}
      return rawBody
    },
    bulkCsvImportSucceeded: function (body) {
      if (!body || typeof body !== 'object') return false
      // Backend returns { success: true, results: { errors: [...] } } for partial imports.
      if (body.success === true || body.ok === true) return true
      if (body.success === false || body.ok === false) return false
      var errors = this.bulkCsvErrorsFromBody(body)
      if (errors.length) return false
      if (body.failed != null && Number(body.failed) > 0) return false
      var results = body.results
      if (results && Number(results.updated) > 0) return true
      return body.success !== false
    },
    applyBulkCsvResponse: function (body) {
      var errors = this.bulkCsvErrorsFromBody(body)
      var ok = this.bulkCsvImportSucceeded(body)
      var level = ok ? (errors.length ? 'warn' : 'ok') : 'err'
      var msg = this.bulkCsvSummaryFromBody(body, errors)
      if (!ok && !errors.length) {
        msg = this.bulkCsvMessageFromBody(body, 'CSV import failed.')
      }
      this.setBulkCsvResult(msg, level, errors)
      if (ok) {
        this.loadProductTrackingPage({ resetPage: false })
      }
    },
    downloadBulkCsvExport: function () {
      var self = this
      var qs = ['for=' + encodeURIComponent('product-tracking-settings')]
      if (this.selectedStorefront !== 'all') {
        qs.push('sellerAccount=' + encodeURIComponent(String(this.selectedStorefront)))
      }
      if (this.selectedMarketplace !== 'all') {
        qs.push('marketplace=' + encodeURIComponent(this.selectedMarketplace))
      }
      var q = (this.searchText || '').trim()
      if (q) qs.push('q=' + encodeURIComponent(q))
      if (this.showIgnoredOnly) qs.push('ignoredOnly=1')
      var exportUrl = '/api/product/settings/export-active-csv?' + qs.join('&')
      var defaultName = 'product-tracking-' + new Date().toISOString().slice(0, 10) + '.csv'

      this.bulkCsvDownloading = true
      this.setBulkCsvResult('', '', [])

      var xhr = new XMLHttpRequest()
      xhr.open('GET', exportUrl, true)
      xhr.responseType = 'blob'
      xhr.onload = function () {
        self.bulkCsvDownloading = false
        if (xhr.status === 200) {
          var blob = xhr.response
          var url = window.URL.createObjectURL(blob)
          var link = document.createElement('a')
          link.href = url
          var cd = xhr.getResponseHeader('Content-Disposition')
          var filename = defaultName
          if (cd) {
            var m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (m && m[1]) filename = m[1].replace(/['"]/g, '').trim() || defaultName
          }
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } else {
          var reader = new FileReader()
          reader.onload = function () {
            var msg = 'Could not download CSV.'
            try {
              var j = JSON.parse(reader.result)
              if (j.message) msg = j.message
            } catch (e) { /* ignore */ }
            self.setBulkCsvResult(msg, 'err', [])
          }
          reader.readAsText(xhr.response)
        }
      }
      xhr.onerror = function () {
        self.bulkCsvDownloading = false
        self.setBulkCsvResult('Download failed. Check your connection and try again.', 'err', [])
      }
      xhr.send()
    },
    onBulkCsvFile: function (e) {
      var input = e.target
      var file = input.files && input.files[0]
      if (!file) return
      if (!this.isBulkCsvFile(file)) {
        this.setBulkCsvResult('Please choose a CSV file (.csv).', 'err')
        input.value = ''
        return
      }
      var self = this
      var formData = new window.FormData()
      formData.append('file', file, file.name || 'product-tracking-import.csv')
      this.bulkCsvUploading = true
      this.setBulkCsvResult('', '', [])
      this.$http.post('/api/product/settings/import-active-csv', formData).then(function (res) {
        var headers = res.headers || {}
        var contentType = headers.get ? headers.get('Content-Type') : (headers['Content-Type'] || headers['content-type'])
        var ct = (contentType || '').toLowerCase()
        if (ct.indexOf('text/html') !== -1 || (typeof res.body === 'string' && res.body.trim().indexOf('<!DOCTYPE') === 0)) {
          self.setBulkCsvResult('Upload failed: server configuration error. Please contact support.', 'err')
          input.value = ''
          self.bulkCsvUploading = false
          return
        }
        var body = self.normalizeBulkCsvImportBody(res.body)
        self.applyBulkCsvResponse(body)
        input.value = ''
        self.bulkCsvUploading = false
      }, function (err) {
        var body = self.normalizeBulkCsvImportBody(err.body)
        var errors = self.bulkCsvErrorsFromBody(body)
        var msg = self.bulkCsvSummaryFromBody(body, errors)
        if (!errors.length) {
          msg = self.bulkCsvMessageFromBody(body, '') ||
            (err.statusText ? String(err.statusText) : '') ||
            'CSV import failed.'
        }
        self.setBulkCsvResult(msg, 'err', errors)
        input.value = ''
        self.bulkCsvUploading = false
      })
    }
  },
  beforeRouteLeave: function (to, from, next) {
    if (!window.dirty) {
      next()
    } else if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      window.dirty = false
      next()
    }
  }
}
</script>

<style scoped>
.pts { position: relative; }

.pts-shell {
  position: relative;
  min-height: 320px;
}
.pts-loading--overlay {
  position: absolute;
  inset: 0;
  z-index: 30;
  min-height: 200px;
  background: rgba(255, 255, 255, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pts-shell-body--inactive {
  pointer-events: none;
  opacity: 0.55;
}
.pts-inline-error {
  background: var(--status-critical-bg);
  border: 1px solid var(--status-critical);
  border-radius: var(--radius-card);
  padding: 12px 16px;
  margin-bottom: 14px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
}
.pts-inline-error-msg {
  margin: 0;
  color: var(--status-critical);
  font-size: 14px;
  flex: 1;
  min-width: 200px;
}

/* ── Loading / Error ── */
.pts-loading {
  display: flex; align-items: center; justify-content: center;
  min-height: 300px;
}
.pts-spinner {
  width: 36px; height: 36px;
  border: 3px solid var(--border-light);
  border-top-color: var(--primary-navy);
  border-radius: 50%;
  animation: pts-spin 0.7s linear infinite;
}
@keyframes pts-spin { to { transform: rotate(360deg); } }

.pts-error {
  background: var(--status-critical-bg); border: 1px solid var(--status-critical);
  border-radius: var(--radius-card); padding: 24px; text-align: center;
}
.pts-error-title { font-weight: 600; color: var(--status-critical); margin: 0 0 4px; }
.pts-error-msg { color: var(--text-secondary); margin: 0 0 16px; font-size: 14px; }

/* ── Header (compact — match tighter settings mock) ── */
.pts-header { margin-bottom: 16px; }
.pts-title {
  font-size: 18px; font-weight: 700; color: var(--primary-navy);
  margin: 0 0 4px;
  letter-spacing: -0.02em;
}
.pts-subtitle { font-size: 12px; color: var(--text-secondary); margin: 0; line-height: 1.45; max-width: 52em; }

/* ── Filter card ── */
.pts-filter-card {
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-card);
  padding: 14px 16px;
  margin-bottom: 10px;
}
.pts-filter-hint {
  font-size: 11px;
  color: var(--text-tertiary);
  margin: 0 0 10px;
  font-style: italic;
  line-height: 1.4;
}

.pts-filter-main {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0 20px;
  row-gap: 10px;
}

.pts-filter-group:first-child {
  flex: 0 1 clamp(260px, 42vw, 360px);
  min-width: 0;
}
.pts-filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 0 1 auto;
  min-width: 0;
}
.pts-filter-group-mp {
  flex: 0 1 clamp(200px, 34vw, 340px);
  min-width: 0;
}

.pts-filter-divider {
  display: none;
  width: 1px;
  align-self: stretch;
  margin: 0 4px 2px;
  background: var(--border-light);
}
@media (min-width: 900px) {
  .pts-filter-divider {
    display: block;
  }
}

.pts-filter-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--text-secondary);
}

.pts-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
}

/** Storefront filter: two pills per row */
.pts-pills--cols-2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  align-content: start;
}

.pts-pills--cols-2 .pts-pill {
  width: 100%;
  justify-content: center;
  box-sizing: border-box;
}

.pts-pills--mp.pts-pills--cols-2 .pts-pill {
  justify-content: flex-start;
  gap: 6px;
  white-space: nowrap;
}

.pts-filter-search-col {
  flex: 1 1 220px;
  min-width: 200px;
  max-width: 300px;
  margin-left: auto;
  padding-top: 14px;
}
@media (max-width: 899px) {
  .pts-filter-search-col {
    flex-basis: 100%;
    max-width: none;
    margin-left: 0;
    padding-top: 0;
  }
}

.pts-summary-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 6px;
  border: 1px solid rgba(217, 222, 230, 0.9);
  background: rgba(246, 248, 251, 0.95);
}

.pts-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  min-height: 26px;
  box-sizing: border-box;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.25;
  border: 1px solid var(--border-medium);
  border-radius: 4px;
  background: var(--bg-white);
  color: rgb(61, 75, 95);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

.pts-pill:hover {
  border-color: rgba(26, 69, 113, 0.55);
  color: var(--primary-navy);
}

.pts-pill.active {
  background: var(--primary-navy);
  border-color: var(--primary-navy);
  color: #fff;
}

.pts-pill i.flag {
  display: inline-block !important;
  flex-shrink: 0;
  margin: 0 !important;
  vertical-align: middle;
  line-height: 11px;
  transform: none;
}

.pts-pill.active i.flag {
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.35);
  border-radius: 1px;
}

.pts-summary {
  font-size: 12px;
  font-weight: 400;
  color: rgb(90, 102, 120);
  line-height: 1.4;
}

.pts-summary strong {
  font-weight: 700;
  color: rgb(45, 58, 75);
}

.pts-search-wrap {
  position: relative; display: flex; align-items: center;
}
.pts-search-icon {
  position: absolute; left: 8px;
  width: 14px;
  height: 14px;
  color: rgb(157, 168, 184);
  pointer-events: none;
}

.pts-search-input {
  padding: 5px 10px 5px 28px;
  font-size: 12px;
  line-height: 1.35;
  border: 1px solid var(--border-light);
  border-radius: 4px;
  outline: none;
  width: 100%;
  min-width: 0;
  max-width: 300px;
  transition: border-color 0.15s;
  background: var(--bg-white);
  color: var(--text-primary);
}

.pts-search-input:focus { border-color: var(--primary-navy); }

.pts-search-input::placeholder {
  color: rgb(157, 168, 184);
}

.pts-show-ignored {
  display: flex; align-items: center; gap: 5px;
  font-size: 12px; font-weight: 500;
  color: rgb(61, 75, 95);
  cursor: pointer;
  user-select: none; white-space: nowrap;
}

.pts-show-ignored input { display: none; }

.pts-show-ignored-box {
  width: 14px; height: 14px;
  border: 1.5px solid var(--border-medium);
  border-radius: 3px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
  position: relative;
}
.pts-show-ignored input:checked ~ .pts-show-ignored-box {
  background: var(--primary-navy);
  border-color: var(--primary-navy);
}
.pts-show-ignored input:checked ~ .pts-show-ignored-box::after {
  content: '';
  width: 3px; height: 6px;
  border: solid #fff; border-width: 0 1.5px 1.5px 0;
  transform: rotate(45deg);
  position: absolute; top: 0;
}

/* ── Bulk CSV ── */
.pts-bulk-card {
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-card);
  padding: 14px 16px;
  margin-bottom: 12px;
}
.pts-bulk-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--primary-navy);
  margin: 0 0 8px;
  letter-spacing: -0.02em;
}
.pts-bulk-desc {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0 0 12px;
  line-height: 1.5;
  max-width: 56em;
}
.pts-bulk-desc code {
  font-size: 11px;
  background: rgba(246, 248, 251, 1);
  padding: 1px 5px;
  border-radius: 3px;
  border: 1px solid var(--border-light);
}
.pts-bulk-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.pts-bulk-file-label {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: 0;
}
.pts-bulk-file-text {
  position: relative;
  z-index: 0;
  pointer-events: none;
}
.pts-bulk-file-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  font-size: 0;
  z-index: 1;
}
.pts-bulk-result {
  margin: 12px 0 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.45;
}
.pts-bulk-result--ok { color: rgb(33, 120, 80); }
.pts-bulk-result--warn { color: rgb(150, 95, 0); }
.pts-bulk-result--err { color: var(--status-critical); }
.pts-bulk-file-label--busy {
  opacity: 0.7;
  pointer-events: none;
}
.pts-bulk-errors {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 12px;
  color: var(--status-critical);
  line-height: 1.45;
  max-height: 200px;
  overflow-y: auto;
}
.pts-bulk-errors li { margin-bottom: 4px; }

/* ── Table ── */
.pts-table-wrap {
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-card) var(--radius-card) 0 0;
  overflow: hidden;
}
.pts-table {
  width: 100%; border-collapse: collapse;
  font-size: 13px;
}
.pts-table thead th {
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--text-secondary);
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--border-light);
  text-align: left;
  white-space: nowrap;
}
.pts-th-product { width: 38%; }
.pts-th-storefront { width: 15%; }
.pts-th-mp { width: 12%; }
.pts-th-shortname { width: 23%; }
.pts-th-ignore { width: 12%; text-align: center !important; }
.pts-cell-storefront {
  font-size: 12.5px;
  color: rgb(61, 75, 95);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pts-bulk-ignore {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 8px 12px;
  margin-bottom: 10px;
  border: 1px solid rgba(217, 222, 230, 0.9);
  border-radius: 6px;
  background: var(--bg-white);
}
.pts-bulk-ignore-scope { font-size: 12px; color: rgb(90, 102, 120); }
.pts-bulk-ignore-scope strong { color: rgb(45, 58, 75); }
.pts-bulk-ignore-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.pts-bulk-ignore .pts-bulk-result { flex-basis: 100%; margin: 0; }

.pts-row { border-bottom: 1px solid var(--border-light); }
.pts-row:last-child { border-bottom: none; }

.pts-row td { padding: 12px 14px; vertical-align: middle; }

.pts-empty {
  text-align: center; padding: 48px 16px !important;
  color: var(--text-tertiary); font-size: 14px;
}

/* Product cell */
.pts-product-wrap { display: flex; align-items: center; gap: 14px; }
.pts-product-img-link { flex-shrink: 0; }
.pts-product-img {
  width: 44px; height: 44px;
  object-fit: contain;
  border-radius: 8px;
  background: var(--bg-subtle);
  display: block;
}
.pts-product-img-placeholder {
  width: 44px; height: 44px;
  border-radius: 8px;
  background: var(--bg-subtle);
  display: flex; align-items: center; justify-content: center;
  color: var(--text-tertiary);
}
.pts-product-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.pts-product-name {
  font-weight: 600; color: var(--text-primary);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 340px;
}
.pts-product-asin { font-size: 12px; color: var(--text-tertiary); }

/* Marketplace cell */
.pts-cell-mp { white-space: nowrap; }
.pts-mp-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}
.pts-mp-emoji {
  line-height: 1;
}


/* Short-hand name cell */
.pts-shortname-input {
  width: 100%;
  padding: 6px 10px;
  font-size: 13px;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  outline: none;
  color: var(--text-primary);
  background: var(--bg-white);
  transition: border-color 0.15s;
}
.pts-shortname-input:focus { border-color: var(--primary-navy); }

/* Ignore toggle */
.pts-cell-ignore { text-align: center; }

.pts-toggle {
  position: relative; display: inline-block;
  width: 44px; height: 24px; cursor: pointer;
}
.pts-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.pts-toggle-slider {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: var(--border-medium);
  border-radius: 24px;
  transition: background-color 0.2s;
}
.pts-toggle-slider::before {
  content: '';
  position: absolute;
  height: 18px; width: 18px;
  left: 3px; bottom: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.pts-toggle input:checked + .pts-toggle-slider {
  background: var(--status-critical);
}
.pts-toggle input:checked + .pts-toggle-slider::before {
  transform: translateX(20px);
}

/* ── Pagination ── */
.pts-pagination {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-top: none;
  border-radius: 0 0 var(--radius-card) var(--radius-card);
}
.pts-page-label { font-size: 13px; color: var(--text-secondary); }
.pts-page-controls { display: flex; align-items: center; gap: 4px; }
.pts-page-btn {
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  background: var(--bg-white);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}
.pts-page-btn:hover:not(:disabled) { border-color: var(--primary-navy); color: var(--primary-navy); }
.pts-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.pts-page-num {
  padding: 0 10px;
  font-size: 13px; font-weight: 500; color: var(--text-primary);
}

/* ── Unsaved changes bar ── */
.pts-changes-bar {
  position: sticky; bottom: 0;
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 24px;
  margin-top: 16px;
  background: var(--bg-white);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-card);
  box-shadow: 0 -4px 16px rgba(0,0,0,0.06);
  z-index: 10;
}
.pts-changes-text {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 500; color: var(--accent-orange);
}
.pts-changes-dot {
  width: 8px; height: 8px;
  background: var(--primary-navy);
  border-radius: 50%;
  flex-shrink: 0;
}
.pts-changes-actions { display: flex; gap: 10px; }

/* Buttons */
.pts-btn {
  padding: 9px 20px;
  font-size: 14px; font-weight: 600;
  border-radius: var(--radius-btn);
  cursor: pointer;
  transition: all 0.15s;
  border: none;
  white-space: nowrap;
}
.pts-btn-outline {
  background: var(--bg-white);
  border: 1px solid var(--border-medium);
  color: var(--text-primary);
}
.pts-btn-outline:hover { border-color: var(--text-secondary); }
.pts-btn-primary {
  background: var(--primary-navy);
  color: #fff;
}
.pts-btn-primary:hover { background: var(--primary-navy-dark); }
.pts-btn-loading { opacity: 0.7; pointer-events: none; }
.pts-btn-sm {
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
}

/* Transition */
.pts-slide-up-enter-active, .pts-slide-up-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.pts-slide-up-enter, .pts-slide-up-leave-to {
  transform: translateY(20px); opacity: 0;
}
</style>



// WEBPACK FOOTER //
// ProductTrackingSettings.vue?12e0179c